/**
 * The controls every settings pane is assembled from. Each writes straight
 * back into settings, so a pane is mostly a list of these with a heading.
 */
import { useState } from 'react';
import { useApp } from '../../store/AppContext.jsx';

/** A titled block with an explanatory line. */
export const Section = ({ title, hint, children, danger, action }) => (
  <div className={`set${danger ? ' zone' : ''}`}>
    {/* Only wrapped when there is something to sit beside, so every other
        pane keeps the markup — and the spacing — it already had. */}
    {title && action
      ? <div className="sethead"><h4>{title}</h4>{action}</div>
      : title ? <h4>{title}</h4> : null}
    {hint ? <p>{hint}</p> : null}
    {children}
  </div>
);

export const Row = ({ children }) => <div className="rowb">{children}</div>;
export const Grid = ({ children }) => <div className="g2">{children}</div>;

/** Labelled numeric setting with min/max clamping and a unit hint. */
export function NumberField({ label, name, unit, min = 0, max = 10000 }) {
  const { S, patchSettings } = useApp();
  return (
    <div className="num">
      <label>{label}</label>
      <input
        type="number"
        min={min}
        max={max}
        value={S[name] ?? 0}
        onChange={e => {
          let v = parseInt(e.target.value, 10);
          if (Number.isNaN(v) || v < min) v = min;
          if (v > max) v = max;
          patchSettings({ [name]: v });
        }}
      />
      {unit ? <small>{unit}</small> : null}
    </div>
  );
}

export function TextField({ label, name, placeholder, hint, area }) {
  const { S, patchSettings } = useApp();
  const props = {
    value: S[name] ?? '',
    placeholder,
    onChange: e => patchSettings({ [name]: e.target.value })
  };
  return (
    <div className="num">
      {label ? <label>{label}</label> : null}
      {area ? <textarea style={{ minHeight: 80 }} {...props} /> : <input {...props} />}
      {hint ? <small>{hint}</small> : null}
    </div>
  );
}

export function SelectField({ label, name, options, hint }) {
  const { S, patchSettings } = useApp();
  return (
    <div className="num">
      <label>{label}</label>
      <select value={S[name] ?? ''} onChange={e => patchSettings({ [name]: e.target.value })}>
        {options.map(o => {
          const [value, text] = Array.isArray(o) ? o : [o, o];
          return <option key={value} value={value}>{text}</option>;
        })}
      </select>
      {hint ? <small>{hint}</small> : null}
    </div>
  );
}

/** A single boolean setting rendered as a checkbox row. */
export function Toggle({ label, name }) {
  const { S, patchSettings } = useApp();
  return (
    <label>
      <input
        type="checkbox"
        checked={!!S[name]}
        onChange={e => patchSettings({ [name]: e.target.checked })}
      />
      {label}
    </label>
  );
}

export const CheckGrid = ({ children }) => <div className="checks">{children}</div>;

/**
 * A membership grid: tick which values from `sourceList` belong in `targetKey`.
 * Used for "counts as active", "counts as paid" and "excluded from metrics".
 */
export function MembershipChecks({ sourceKey, targetKey }) {
  const { S, patchSettings } = useApp();
  const selected = new Set(S[targetKey] || []);

  return (
    <CheckGrid>
      {(S[sourceKey] || []).map(v => (
        <label key={v}>
          <input
            type="checkbox"
            checked={selected.has(v)}
            onChange={e => {
              const next = new Set(selected);
              if (e.target.checked) next.add(v); else next.delete(v);
              patchSettings({ [targetKey]: [...next] });
            }}
          />
          {v}
        </label>
      ))}
    </CheckGrid>
  );
}

/**
 * An editable list of short strings. Removing a value also strips it from the
 * membership lists that referenced it, so nothing is left pointing at a value
 * that no longer exists.
 */
export function Chips({ listKey }) {
  const { S, updateSettings, toast } = useApp();
  const [text, setText] = useState('');
  const values = S[listKey] || [];

  const remove = index => {
    if (values.length <= 1) { toast('Keep at least one value'); return; }
    const removed = values[index];
    updateSettings(next => {
      next[listKey] = (next[listKey] || []).filter((_, i) => i !== index);
      ['excluded', 'liveStatuses', 'paidValues'].forEach(k => {
        next[k] = (next[k] || []).filter(x => x !== removed);
      });
    });
  };

  const add = () => {
    const v = text.trim();
    if (!v) return;
    if (values.includes(v)) { toast('Already in the list'); return; }
    updateSettings(next => { next[listKey] = [...(next[listKey] || []), v]; });
    setText('');
    toast('Added');
  };

  return (
    <div className="chips">
      {values.map((v, i) => (
        <span className="chip" key={v}>
          {v}
          <button type="button" onClick={() => remove(i)}>✕</button>
        </span>
      ))}
      <input
        placeholder="Add…"
        value={text}
        onChange={e => setText(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
      />
    </div>
  );
}
